import { CareRecipientListJson, CareRecipientSortCondition } from "../../../../domain/care_recipient/CareRecipientWithDataSyncInformation";
import { CareRecipientWithSensorJson } from "../../../../domain/care_recipient/CareRecipientWithSensor";
import { createSlice } from "@reduxjs/toolkit";
import {
    ActionType,
    getCareRecipientListAction,
    getCareRecipientInfoAction,
    updateCareRecipientInfoAction,
    registerCareRecipientInfoAction,
    deleteCareRecipientAction,
    getAllAlertsAction,
    updatePeriodAction,
    disableAlertAction,
    disableAlertsAction,
    keywordSearchAction,
    hideDataSyncFinishedAction,
    sortAction,
    setPageAction,
    checkDisableAlertAction,
    toggleAllCheckAlertsAction,
    toggleAlertDisplayAction
} from "./actions";
import { DateTime } from "../../../../domain/fundamental/DateTime";
import { compareString } from "../../../../app/funcs/compare";

const MAX_SHOW_ALERT_COUNT = 5;

export type AdminCareRecipientState = Readonly<{
    careRecipientList: CareRecipientListJson | null,
    searchedCareRecipientList: {
        list: CareRecipientListJson | null,
        keyword: string,
        hideDataSyncFinished: boolean
    },
    careRecipientInfo: CareRecipientWithSensorJson | null,
    processing: number,
    processingAlerts: number,
    alerts: {
        careRecipient: {
            id: string,
            name: string,
            dataSyncInformation: {
                dataSyncPeriod: {
                    startTime: string | null,
                    endTime: string | null
                },
                houseId: string
            }
        }, 
        period: {
            start: string,
            end: string,
        } | null,
        alert: {
            id: string,
            date: string,
            category: string,
            message: string,
            display: boolean,
            createdFrom: string,
        }
    }[] | null,
    displayAlertsPage: number,
    checkedAlertIds: string[],
    processingMonth: number,
    settingMonth: string | null
}>;

const adminCareRecipientInitialState: AdminCareRecipientState = {
    careRecipientList: null,
    searchedCareRecipientList: {
        list: null,
        keyword: "",
        hideDataSyncFinished: false
    },
    careRecipientInfo: null,
    processing: 0,
    processingAlerts: 0,
    alerts: null,
    displayAlertsPage: 0,
    checkedAlertIds: [],
    processingMonth: 0,
    settingMonth: null
};

const slice = createSlice({
    name: ActionType.base,
    initialState: adminCareRecipientInitialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(
                getCareRecipientListAction.pending,
                (state) => {
                    state.careRecipientList = null;
                    state.searchedCareRecipientList = adminCareRecipientInitialState.searchedCareRecipientList;
                    state.processing += 1;
                }
            )
            .addCase(
                getCareRecipientListAction.fulfilled,
                (state, action) => {
                    state.careRecipientList = action.payload.careRecipients;
                    state.searchedCareRecipientList = {
                        list: action.payload.careRecipients,
                        keyword: "",
                        hideDataSyncFinished: false
                    };
                    state.processing -= 1;
                }
            )
            .addCase(
                getCareRecipientListAction.rejected,
                (state) => {
                    state.processing -= 1;
                }
            )
            .addCase(
                getCareRecipientInfoAction.pending,
                (state) => {
                    state.careRecipientInfo = null;
                    state.processing += 1;
                }
            )
            .addCase(
                getCareRecipientInfoAction.fulfilled,
                (state, action) => {
                    state.careRecipientInfo = action.payload.careRecipientInfo;
                    state.processing -= 1;
                }
            )
            .addCase(
                getCareRecipientInfoAction.rejected,
                (state) => {
                    state.processing -= 1;
                }
            )
            .addCase(
                updateCareRecipientInfoAction.pending,
                (state) => {
                    state.processing += 1;
                }
            )
            .addCase(
                updateCareRecipientInfoAction.fulfilled,
                (state, action) => {
                    state.careRecipientList = null;
                    state.careRecipientInfo = { ...action.payload.careRecipientInfo };
                    state.processing -= 1;
                }
            )
            .addCase(
                updateCareRecipientInfoAction.rejected,
                (state) => {
                    state.processing -= 1;
                }
            )
            .addCase(
                registerCareRecipientInfoAction.pending,
                (state) => {
                    state.processing += 1;
                }
            )
            .addCase(
                registerCareRecipientInfoAction.fulfilled,
                (state, action) => {
                    state.careRecipientList = null;
                    state.careRecipientInfo = { ...action.payload.careRecipientInfo, alertPeriod: null };
                    state.processing -= 1;
                }
            )
            .addCase(
                registerCareRecipientInfoAction.rejected,
                (state) => {
                    state.processing -= 1;
                }
            )
            .addCase(
                deleteCareRecipientAction.pending,
                (state) => {
                    state.processing += 1;
                }
            )
            .addCase(
                deleteCareRecipientAction.fulfilled,
                (state, action) => {
                    const { careRecipientId } = action.payload;
                    state.careRecipientList = state.careRecipientList?.filter(careRecipient => careRecipient.id !== careRecipientId) ?? null;
                    state.searchedCareRecipientList = {
                        ...state.searchedCareRecipientList,
                        list: state.searchedCareRecipientList.list?.filter(careRecipient => careRecipient.id !== careRecipientId) ?? null
                    };
                    state.processing -= 1;
                }
            )
            .addCase(
                deleteCareRecipientAction.rejected,
                (state) => {
                    state.processing -= 1;
                }
            ).addCase(
                getAllAlertsAction.fulfilled,
                (state, action) => {
                    const { payload } = action;
                    state.alerts = payload.alerts;
                    state.processingAlerts -= 1;
                }
            ).addCase(
                getAllAlertsAction.pending,
                (state, _) => {
                    state.processingAlerts += 1;
                }
            ).addCase(
                getAllAlertsAction.rejected,
                (state, _) => {
                    state.processingAlerts -= 1;
                }
            ).addCase(
                setPageAction.fulfilled,
                (state, action) => {
                    const { page } = action.payload;
                    state.displayAlertsPage = page;
                }
            ).addCase(
                updatePeriodAction.pending,
                (state, _) => {
                    state.processingMonth += 1;
                }
            ).addCase(
                updatePeriodAction.fulfilled,
                (state, action) => {
                    const { payload } = action;
                    const monthSettingHistory = payload.monthSettingHistory;
                    state.processingMonth -= 1;
                    state.careRecipientList = state.careRecipientList?.map(careRecipient => {
                        if (careRecipient.id === monthSettingHistory.careRecipientId) {
                            return { ...careRecipient, alertPeriod: { start: monthSettingHistory.startDate, end: monthSettingHistory.endDate } };
                        }
                        return careRecipient;
                    }) ?? null;
                    state.searchedCareRecipientList = {
                        ...state.searchedCareRecipientList,
                        list: state.searchedCareRecipientList.list?.map(careRecipient => {
                            if (careRecipient.id === monthSettingHistory.careRecipientId) {
                                return { ...careRecipient, alertPeriod: { start: monthSettingHistory.startDate, end: monthSettingHistory.endDate } };
                            }
                            return careRecipient;
                        }) ?? null
                    };
                    state.alerts = state.alerts?.map(alert => {
                        if (alert.careRecipient.id !== monthSettingHistory.careRecipientId) {
                            return alert;
                        }
                        return { ...alert, period: { start: monthSettingHistory.startDate, end: monthSettingHistory.endDate } };
                    }) ?? null;
                }
            ).addCase(
                updatePeriodAction.rejected,
                (state, _) => {
                    state.processing -= 1;
                }
            ).addCase(
                checkDisableAlertAction.fulfilled,
                (state, action) => {
                    const { alertId } = action.payload;
                    state.checkedAlertIds = state.checkedAlertIds.includes(alertId) ? state.checkedAlertIds.filter(checkedAlertId => checkedAlertId !== alertId) : [...state.checkedAlertIds, alertId];
                }
            ).addCase(
                toggleAllCheckAlertsAction.fulfilled,
                (state, action) => {
                    const { check } = action.payload;
                    state.checkedAlertIds = check && state.alerts ? state.alerts.map(alert => alert.alert.id) : [];
                }
            ).addCase(
                disableAlertAction.fulfilled,
                (state, action) => {
                    const { payload } = action;
                    state.alerts = state.alerts?.filter(alert => alert.alert.id !== payload.alertId) ?? null;
                    state.processingAlerts -= 1;
                }
            ).addCase(
                disableAlertsAction.fulfilled,
                (state, action) => {
                    const { alertIdList } = action.payload;
                    const newAlerts = state.alerts?.filter(alert => !alertIdList.includes(alert.alert.id)) ?? null;
                    state.alerts = newAlerts;
                    state.checkedAlertIds = [];

                    // アラート解除後に表示ページがページ数の最大値を超えないようにする
                    const pageMaxIndex = Math.ceil((newAlerts?.length ?? 0) / MAX_SHOW_ALERT_COUNT) - 1;
                    if (state.displayAlertsPage > pageMaxIndex) {
                        state.displayAlertsPage = pageMaxIndex;
                    }
                }
            ).addCase(
                toggleAlertDisplayAction.fulfilled,
                (state, action) => {
                    const { alertId } = action.payload;
                    state.alerts = state.alerts?.map(alert => {
                        if (alert.alert.id === alertId) {
                            return {
                                ...alert,
                                alert: {
                                    ...alert.alert,
                                    display: !alert.alert.display
                                }
                            };
                        }
                        return alert;
                    }) ?? null;
                }
            ).addCase(
                keywordSearchAction.fulfilled,
                (state, action) => {
                    const { searchKeyword } = action.payload;

                    const searchedCareRecipientList = searchCareRecipientList(state.careRecipientList, state.searchedCareRecipientList.hideDataSyncFinished, searchKeyword);

                    state.searchedCareRecipientList = {
                        ...state.searchedCareRecipientList,
                        list: searchedCareRecipientList,
                        keyword: searchKeyword
                    };
                }
            ).addCase(
                hideDataSyncFinishedAction.fulfilled,
                (state, action) => {
                    const { hideDataSyncFinished } = action.payload;

                    const searchedCareRecipientList = searchCareRecipientList(state.careRecipientList, hideDataSyncFinished, state.searchedCareRecipientList.keyword);

                    state.searchedCareRecipientList = {
                        ...state.searchedCareRecipientList,
                        list: searchedCareRecipientList,
                        hideDataSyncFinished: hideDataSyncFinished
                    };
                }
            ).addCase(
                sortAction.fulfilled,
                (state, action) => {
                    const { sortCondition, asc } = action.payload;

                    switch (sortCondition) {
                        case CareRecipientSortCondition.HOUSE_ID:
                            state.searchedCareRecipientList = {
                                ...state.searchedCareRecipientList,
                                list: state.searchedCareRecipientList.list?.slice().sort((firstData, secondData) => (
                                    compareString(asc ? "asc" : "desc", firstData.houseId, secondData.houseId)
                                )) ?? null
                            };
                            break;
                        case CareRecipientSortCondition.CARE_RECIPIENT_NAME:
                            state.searchedCareRecipientList = {
                                ...state.searchedCareRecipientList,
                                list: state.searchedCareRecipientList.list?.slice().sort((firstData, secondData) => (
                                    compareString(asc ? "asc" : "desc", firstData.name, secondData.name)
                                )) ?? null
                            };
                            break;
                        case CareRecipientSortCondition.DATA_SYNC_START_TIME:
                            state.searchedCareRecipientList = {
                                ...state.searchedCareRecipientList,
                                list: state.searchedCareRecipientList.list?.slice().sort((firstData, secondData) => (
                                    compareString(asc ? "asc" : "desc", firstData.dataSyncStartTime ?? "", secondData.dataSyncStartTime ?? "")
                                )) ?? null
                            };
                            break;
                    }
                }
            );
    }
});

const searchCareRecipientList = (careRecipientList: CareRecipientListJson | null, hideDataSyncFinished: boolean, searchKeyword: string) => {
    if (!careRecipientList) return null;

    // データ連携終了者をフィルター
    const now = DateTime.now().format();
    const hideDataSyncFinishedList = hideDataSyncFinished ? careRecipientList.filter(careRecipient => (
        careRecipient.dataSyncEndTime === null || DateTime.fromString(careRecipient.dataSyncEndTime).format() > now
    )) : careRecipientList;

    // キーワードで氏名・ハウスIDを検索
    const searchedCareRecipientList = hideDataSyncFinishedList?.filter(careRecipient => (
        careRecipient.name.includes(searchKeyword) || careRecipient.houseId?.includes(searchKeyword)
    ));

    return searchedCareRecipientList;
};

export const adminCareRecipientReducer = slice.reducer;